import {
  usePricingPlanActive,
  usePricingPlanPaidActive,
  usePricingPlanTrialActive,
} from '~/modules/pricing';
import { IPlanKey, IShopPlanState } from '~/modules/pricing/types';
import { useMemo } from 'react';

export const MAPPING_PLAN_KEY_TO_STATE: Record<IPlanKey, IShopPlanState> = {
  [IPlanKey.PAGE_OPT]: IShopPlanState.ACTIVE_PAGE_OPT,
  [IPlanKey.CONTENT_TESTING]: IShopPlanState.ACTIVE_PAGE_OPT,
  [IPlanKey.FUNNEL_OPT]: IShopPlanState.ACTIVE_FUNNEL_OPT,
  [IPlanKey.PROFIT_OPT]: IShopPlanState.ACTIVE_PROFIT_OPT,
  [IPlanKey.TRIAL_FUNNEL_OPT]: IShopPlanState.TRIAL_FUNNEL_OPT,
  [IPlanKey.TRIAL_PAGE_OPT]: IShopPlanState.TRIAL_PAGE_OPT,
  [IPlanKey.TRIAL_PROFIT_OPT]: IShopPlanState.TRIAL_PROFIT_OPT,
};

export interface IPricingPlanActiveState {
  shopPlanState: IShopPlanState;
  isTrialCompletedExpired: boolean;
}

export const usePricingPlanActiveState = (): IPricingPlanActiveState => {
  const { activePlanKey, isLoading } = usePricingPlanActive();
  const { isPaid, isPaidPageOpt, isPaidFunnelOpt } = usePricingPlanPaidActive();
  const { trialPlanKey, isTrial, isTrialExpired, isTrialFunnelOpt, isTrialPageOpt } =
    usePricingPlanTrialActive();

  const shopPlanState = useMemo(() => {
    if (isLoading) return IShopPlanState.LOADING_PLAN;
    if (!isPaid && isTrial && isTrialExpired) return IShopPlanState.TRIAL_EXPIRED;
    if (!isPaid && isTrial && trialPlanKey) return MAPPING_PLAN_KEY_TO_STATE[trialPlanKey];
    if (isPaid && !isTrial) return MAPPING_PLAN_KEY_TO_STATE[activePlanKey];

    if (isPaidPageOpt && isTrialPageOpt) {
      return IShopPlanState.ACTIVE_PAGE_OPT_AND_TRIAL_PAGE_OPT;
    }
    if (isPaidPageOpt && isTrialFunnelOpt) {
      return IShopPlanState.ACTIVE_PAGE_OPT_AND_TRIAL_FUNNEL_OPT;
    }
    if (isPaidFunnelOpt && isTrialFunnelOpt) {
      return IShopPlanState.ACTIVE_FUNNEL_OPT_AND_TRIAL_FUNNEL_OPT;
    }

    return IShopPlanState.TRIAL_FUNNEL_OPT;
  }, [
    isLoading,
    isTrial,
    isTrialExpired,
    trialPlanKey,
    isPaid,
    activePlanKey,
    isPaidPageOpt,
    isTrialPageOpt,
    isTrialFunnelOpt,
    isPaidFunnelOpt,
  ]);

  return {
    shopPlanState,
    isTrialCompletedExpired: shopPlanState === IShopPlanState.TRIAL_EXPIRED,
  };
};
